/**
 * User Dashboard
 * Dashboard for normal users with limited functionality:
 * - Overview (credits, account status)
 * - Download desktop app
 * - Buy credits
 * - Earn credits (referrals)
 * - Account settings
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Home, Download, CreditCard, Gift, User, LogOut, 
  Wallet, TrendingUp, Copy, Check, Monitor, Apple, Terminal,
  Shield, Zap, Wifi, HardDrive, ExternalLink, ArrowRight,
  RefreshCw, ChevronRight, Settings, Star, Crown, Loader2,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { SyncLogoWithText } from '@/components/Logo';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// PayPal icon component
const PayPalIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.644h6.378c2.97 0 5.27.858 6.397 2.396.53.724.828 1.53.89 2.412.066.949-.072 2.03-.408 3.217-.495 1.75-1.337 3.15-2.5 4.162-1.11.964-2.53 1.58-4.218 1.83-.357.052-.724.078-1.095.078H8.59a.768.768 0 0 0-.758.645l-.757 4.867a.64.64 0 0 1-.632.553h-3.25l.883-5.746h3.034l-.034.22zm5.557-11.83c-.05.346-.156.68-.316.998-.408.815-1.09 1.244-2.028 1.244h-1.31l.687-4.428h1.36c1.014 0 1.637.505 1.637 1.377 0 .298-.01.553-.03.809z"/>
  </svg>
);

// Platform download info
const platforms = [
  {
    id: 'windows',
    name: 'Windows',
    icon: Monitor,
    extension: '.msi',
    requirements: 'Windows 10 or later (64-bit)',
    size: '~15 MB',
  },
  {
    id: 'macos',
    name: 'macOS',
    icon: Apple,
    extension: '.dmg',
    requirements: 'macOS 10.13 or later',
    size: '~12 MB',
  },
  {
    id: 'linux',
    name: 'Linux',
    icon: Terminal,
    extension: '.AppImage',
    requirements: 'Ubuntu 18.04+ or equivalent',
    size: '~14 MB',
  },
];

const desktopFeatures = [
  { icon: Zap, title: 'Lower Latency', desc: 'Direct exchange connections' },
  { icon: Shield, title: 'Secure', desc: 'Keys encrypted locally' },
  { icon: Wifi, title: 'Your IP', desc: 'No server whitelisting' },
  { icon: HardDrive, title: 'Offline Mode', desc: 'Paper trade offline' },
];

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, wallet, logout, refreshWallet, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [referralCode, setReferralCode] = useState('');
  const [referralStats, setReferralStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [detectedPlatform, setDetectedPlatform] = useState(null);
  const [latestRelease, setLatestRelease] = useState(null);
  
  // Credit packages state
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState({
    stripe_enabled: false,
    paypal_enabled: false,
    crypto_enabled: false,
    paypal_client_id: null
  });
  const [processingPayPal, setProcessingPayPal] = useState(false);
  const [packagesLoading, setPackagesLoading] = useState(true);

  useEffect(() => {
    // Detect user's platform
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('win')) setDetectedPlatform('windows');
    else if (userAgent.includes('mac')) setDetectedPlatform('macos');
    else if (userAgent.includes('linux')) setDetectedPlatform('linux');
  }, []);

  useEffect(() => {
    fetchReferralData();
    fetchTransactions();
    fetchLatestRelease();
    fetchPackages();
    fetchPaymentMethods();
  }, []);

  const fetchLatestRelease = async () => {
    try {
      const response = await axios.get(`${API}/desktop/latest-version`);
      if (response.data.available) {
        setLatestRelease(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch latest release:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      setPackagesLoading(true);
      const response = await axios.get(`${API}/payments/packages`);
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    } finally {
      setPackagesLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/payments/methods`);
      setPaymentMethods(response.data);
      
      if (response.data.stripe_enabled) {
        setPaymentMethod('stripe');
      } else if (response.data.paypal_enabled) {
        setPaymentMethod('paypal');
      } else if (response.data.crypto_enabled) {
        setPaymentMethod('crypto');
      }
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
      setPaymentMethods({ stripe_enabled: true, paypal_enabled: false, crypto_enabled: false });
      setPaymentMethod('stripe');
    }
  };

  const fetchReferralData = async () => {
    try {
      const response = await axios.get(`${API}/referrals/my-code`);
      setReferralCode(response.data.referral_code);
      
      const statsResponse = await axios.get(`${API}/referrals/stats`);
      setReferralStats(statsResponse.data);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/user/transactions?limit=10`);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const handleCopyReferralCode = () => {
    const referralLink = `${window.location.origin}/auth?mode=register&ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = (platform) => {
    // Get download URL from latest release
    if (!latestRelease?.download_urls) {
      toast.error('No downloads available yet. Please check back later.');
      return;
    }
    
    // Map platform id to download URL key
    const urlKeyMap = {
      'windows': 'windows',
      'macos': 'macos-intel', // Default to Intel, could detect ARM
      'linux': 'linux'
    };
    
    const urlKey = urlKeyMap[platform.id];
    const downloadUrl = latestRelease.download_urls[urlKey];
    
    if (!downloadUrl) {
      toast.error(`Download for ${platform.name} is not available yet.`);
      return;
    }
    
    window.open(downloadUrl, '_blank');
    toast.success(`Downloading OMNITrade v${latestRelease.version} for ${platform.name}...`);
  };

  // Stripe checkout handler
  const handleStripeCheckout = async (packageId) => {
    setLoading(true);
    setSelectedPackage(packageId);
    
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Stripe checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  // PayPal create order handler
  const createPayPalOrder = async (packageId) => {
    try {
      const response = await axios.post(`${API}/paypal/create-order`, {
        package_id: packageId
      });
      return response.data.order_id;
    } catch (error) {
      console.error('PayPal create order error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create PayPal order');
      throw error;
    }
  };

  // PayPal approve handler
  const onPayPalApprove = async (data) => {
    setProcessingPayPal(true);
    try {
      const response = await axios.post(`${API}/paypal/capture-order`, {
        order_id: data.orderID
      });
      
      if (response.data.status === 'completed') {
        toast.success(`Payment successful! ${response.data.credits_added} credits added to your account.`);
        await refreshWallet();
      } else {
        toast.info(`Payment status: ${response.data.status}`);
      }
    } catch (error) {
      console.error('PayPal capture error:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete PayPal payment');
    } finally {
      setProcessingPayPal(false);
    }
  };

  const getPackageIcon = (index) => {
    switch (index) {
      case 0: return <Zap className="h-8 w-8" />;
      case 1: return <Star className="h-8 w-8" />;
      case 2: return <Crown className="h-8 w-8" />;
      default: return <CreditCard className="h-8 w-8" />;
    }
  };

  const getPackageColor = (index) => {
    switch (index) {
      case 0: return 'from-blue-500 to-blue-600';
      case 1: return 'from-purple-500 to-purple-600';
      case 2: return 'from-amber-500 to-orange-600';
      default: return 'from-green-500 to-emerald-600';
    }
  };

  const getBestValue = (pkgs) => {
    if (pkgs.length === 0) return null;
    return pkgs.reduce((best, pkg) => 
      pkg.price_per_credit < best.price_per_credit ? pkg : best
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Sort platforms to show detected one first
  const sortedPlatforms = [...platforms].sort((a, b) => {
    if (a.id === detectedPlatform) return -1;
    if (b.id === detectedPlatform) return 1;
    return 0;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#0f2847] to-[#0a1628]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0a1628]/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <SyncLogoWithText variant="dark" />
          
          <div className="flex items-center gap-4">
            {/* Credits Display */}
            <div className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10">
              <Wallet className="w-4 h-4 text-[#5A9BD5]" />
              <span className="text-white font-medium">{wallet?.balance_credits?.toFixed(2) || '0.00'}</span>
              <span className="text-gray-400 text-sm">credits</span>
            </div>
            
            {/* Admin Link (if admin) */}
            {isAdmin && (
              <Button 
                variant="outline" 
                size="sm"
                className="border-[#5A9BD5]/50 text-[#5A9BD5] hover:bg-[#5A9BD5]/10"
                onClick={() => navigate('/admin')}
                data-testid="admin-panel-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Admin Panel
              </Button>
            )}
            
            {/* User Menu */}
            <div className="flex items-center gap-2">
              <span className="hidden sm:block text-gray-400 text-sm">{user?.email}</span>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-gray-400 hover:text-white"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          {/* Tab Navigation */}
          <TabsList className="bg-white/5 border border-white/10 p-1 rounded-xl">
            <TabsTrigger 
              value="overview" 
              className="data-[state=active]:bg-[#5A9BD5] data-[state=active]:text-white rounded-lg px-6"
              data-testid="tab-overview"
            >
              <Home className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            {/* Trading Tab - Admin Only */}
            {isAdmin && (
              <TabsTrigger 
                value="trading" 
                className="data-[state=active]:bg-[#5A9BD5] data-[state=active]:text-white rounded-lg px-6"
                data-testid="tab-trading"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Trading
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="download" 
              className="data-[state=active]:bg-[#5A9BD5] data-[state=active]:text-white rounded-lg px-6"
              data-testid="tab-download"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </TabsTrigger>
            <TabsTrigger 
              value="credits" 
              className="data-[state=active]:bg-[#5A9BD5] data-[state=active]:text-white rounded-lg px-6"
              data-testid="tab-credits"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Buy Credits
            </TabsTrigger>
            <TabsTrigger 
              value="referrals" 
              className="data-[state=active]:bg-[#5A9BD5] data-[state=active]:text-white rounded-lg px-6"
              data-testid="tab-referrals"
            >
              <Gift className="w-4 h-4 mr-2" />
              Earn Credits
            </TabsTrigger>
            <TabsTrigger 
              value="account" 
              className="data-[state=active]:bg-[#5A9BD5] data-[state=active]:text-white rounded-lg px-6"
              data-testid="tab-account"
            >
              <User className="w-4 h-4 mr-2" />
              Account
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Credit Balance Card */}
              <Card className="bg-gradient-to-br from-[#5A9BD5]/20 to-[#5A9BD5]/5 border-[#5A9BD5]/30">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Wallet className="w-8 h-8 text-[#5A9BD5]" />
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={refreshWallet}
                      className="text-[#5A9BD5] hover:bg-[#5A9BD5]/10"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="text-3xl font-bold text-white mb-1">
                    {wallet?.balance_credits?.toFixed(2) || '0.00'}
                  </div>
                  <div className="text-gray-400 text-sm">Available Credits</div>
                  <Button 
                    className="w-full mt-4 bg-[#5A9BD5] hover:bg-[#4A8BC5]"
                    onClick={() => setActiveTab('credits')}
                  >
                    Buy More Credits
                  </Button>
                </CardContent>
              </Card>

              {/* Quick Actions Card */}
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <Button 
                      variant="outline" 
                      className="w-full justify-between border-white/20 text-white hover:bg-white/5"
                      onClick={() => setActiveTab('download')}
                    >
                      <span className="flex items-center gap-2">
                        <Download className="w-4 h-4" />
                        Download Desktop App
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-between border-white/20 text-white hover:bg-white/5"
                      onClick={() => setActiveTab('referrals')}
                    >
                      <span className="flex items-center gap-2">
                        <Gift className="w-4 h-4" />
                        Invite Friends
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Referral Stats Card */}
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Gift className="w-5 h-5 text-green-400" />
                    <h3 className="text-lg font-semibold text-white">Referral Earnings</h3>
                  </div>
                  <div className="text-2xl font-bold text-green-400 mb-1">
                    +{referralStats?.total_bonus_earned?.toFixed(2) || '0.00'}
                  </div>
                  <div className="text-gray-400 text-sm mb-4">Credits earned from referrals</div>
                  <div className="text-sm text-gray-400">
                    <span className="text-white font-medium">{referralStats?.total_referrals || 0}</span> friends invited
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Transactions */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Recent Activity</CardTitle>
                <CardDescription className="text-gray-400">Your latest transactions</CardDescription>
              </CardHeader>
              <CardContent>
                {transactions.length > 0 ? (
                  <div className="space-y-3">
                    {transactions.slice(0, 5).map((tx, idx) => (
                      <div key={idx} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            tx.amount_credits > 0 ? 'bg-green-500/20' : 'bg-red-500/20'
                          }`}>
                            {tx.amount_credits > 0 ? (
                              <TrendingUp className="w-5 h-5 text-green-400" />
                            ) : (
                              <CreditCard className="w-5 h-5 text-red-400" />
                            )}
                          </div>
                          <div>
                            <div className="text-white font-medium text-sm">{tx.description || tx.type}</div>
                            <div className="text-gray-500 text-xs">
                              {new Date(tx.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <div className={`font-semibold ${tx.amount_credits > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {tx.amount_credits > 0 ? '+' : ''}{tx.amount_credits?.toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No transactions yet
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trading Tab - Admin Only */}
          {isAdmin && (
            <TabsContent value="trading" className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">Backend Trading Bot</h2>
                <p className="text-gray-400">Admin-only web-based trading interface for testing and monitoring.</p>
              </div>
              
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-8 text-center">
                  <BarChart3 className="w-16 h-16 text-[#5A9BD5] mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">Trading Dashboard</h3>
                  <p className="text-gray-400 mb-6 max-w-md mx-auto">
                    Access the full trading interface with real-time price feeds, bot controls, and trade execution.
                  </p>
                  <Button 
                    onClick={() => navigate('/trading')}
                    className="bg-[#5A9BD5] hover:bg-[#4A8BC5] text-white px-8"
                    data-testid="open-trading-btn"
                  >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Open Trading Dashboard
                  </Button>
                </CardContent>
              </Card>
              
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                      <span className="text-white font-medium">Live Trading</span>
                    </div>
                    <p className="text-gray-400 text-sm">Execute trades on connected exchanges in real-time.</p>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <Shield className="w-5 h-5 text-blue-400" />
                      <span className="text-white font-medium">Paper Trading</span>
                    </div>
                    <p className="text-gray-400 text-sm">Test strategies with simulated funds before going live.</p>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <Wifi className="w-5 h-5 text-yellow-400" />
                      <span className="text-white font-medium">Real-time Data</span>
                    </div>
                    <p className="text-gray-400 text-sm">WebSocket-powered price updates from exchanges.</p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Download Tab */}
          <TabsContent value="download" className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Download OMNITrade Desktop</h2>
              <p className="text-gray-400">Run the trading bot directly on your machine for lower latency and better security.</p>
              {latestRelease && (
                <p className="text-[#5A9BD5] text-sm mt-2">Latest Version: v{latestRelease.version}</p>
              )}
            </div>

            {!latestRelease?.available && (
              <Alert className="bg-yellow-500/10 border-yellow-500/30 mb-6">
                <AlertDescription className="text-yellow-400">
                  Desktop app downloads are currently being prepared. Please check back soon or contact support.
                </AlertDescription>
              </Alert>
            )}

            {/* Features */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {desktopFeatures.map((feature, idx) => (
                <Card key={idx} className="bg-white/5 border-white/10 text-center">
                  <CardContent className="p-4">
                    <feature.icon className="w-8 h-8 text-[#5A9BD5] mx-auto mb-2" />
                    <div className="text-white font-medium text-sm">{feature.title}</div>
                    <div className="text-gray-500 text-xs">{feature.desc}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Download Cards */}
            <div className="grid md:grid-cols-3 gap-6">
              {sortedPlatforms.map((platform) => {
                const isDetected = platform.id === detectedPlatform;
                const Icon = platform.icon;
                const urlKeyMap = { 'windows': 'windows', 'macos': 'macos-intel', 'linux': 'linux' };
                const hasDownload = latestRelease?.download_urls?.[urlKeyMap[platform.id]];
                
                return (
                  <Card 
                    key={platform.id}
                    className={`relative ${
                      isDetected 
                        ? 'bg-[#5A9BD5]/10 border-[#5A9BD5]/50' 
                        : 'bg-white/5 border-white/10'
                    }`}
                  >
                    {isDetected && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#5A9BD5] text-white text-xs font-medium rounded-full">
                        Recommended
                      </div>
                    )}
                    <CardContent className="p-6 text-center">
                      <Icon className={`w-12 h-12 mx-auto mb-4 ${isDetected ? 'text-[#5A9BD5]' : 'text-gray-400'}`} />
                      <h3 className="text-xl font-semibold text-white mb-1">{platform.name}</h3>
                      <p className="text-gray-500 text-sm mb-4">{platform.requirements}</p>
                      
                      <div className="space-y-2 text-sm mb-6">
                        <div className="flex justify-between text-gray-400">
                          <span>Format</span>
                          <span className="text-white">{platform.extension}</span>
                        </div>
                        {latestRelease && (
                          <div className="flex justify-between text-gray-400">
                            <span>Version</span>
                            <span className="text-white">v{latestRelease.version}</span>
                          </div>
                        )}
                      </div>
                      
                      <Button 
                        onClick={() => handleDownload(platform)}
                        disabled={!hasDownload}
                        className={`w-full ${
                          !hasDownload 
                            ? 'bg-gray-600 cursor-not-allowed opacity-50'
                            : isDetected 
                              ? 'bg-[#5A9BD5] hover:bg-[#4A8BC5]' 
                              : 'bg-white/10 hover:bg-white/20'
                        }`}
                        data-testid={`download-${platform.id}-btn`}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {hasDownload ? 'Download' : 'Coming Soon'}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Buy Credits Tab */}
          <TabsContent value="credits" className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Buy Credits</h2>
              <p className="text-gray-400">Purchase credits to use with the trading bot.</p>
              <div className="flex items-center justify-center gap-2 mt-4 bg-white/5 px-4 py-2 rounded-lg inline-flex">
                <Wallet className="h-5 w-5 text-[#5A9BD5]" />
                <span className="text-white font-medium">{wallet?.balance_credits?.toFixed(0) || 0} credits</span>
              </div>
            </div>

            {/* Payment Method Selector */}
            {!packagesLoading && (paymentMethods.stripe_enabled || paymentMethods.paypal_enabled) && (
              <Card className="bg-white/5 border-white/10 mb-6">
                <CardContent className="p-4">
                  <div className="flex items-center justify-center gap-4">
                    <span className="text-gray-400 text-sm">Payment Method:</span>
                    <div className="flex gap-2">
                      {paymentMethods.stripe_enabled && (
                        <Button
                          variant={paymentMethod === 'stripe' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPaymentMethod('stripe')}
                          className={paymentMethod === 'stripe' ? 'bg-[#5A9BD5]' : 'border-white/20 text-white'}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Card
                        </Button>
                      )}
                      {paymentMethods.paypal_enabled && (
                        <Button
                          variant={paymentMethod === 'paypal' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPaymentMethod('paypal')}
                          className={paymentMethod === 'paypal' ? 'bg-[#0070ba]' : 'border-white/20 text-white'}
                        >
                          <PayPalIcon className="w-4 h-4 mr-2" />
                          PayPal
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Credit Packages */}
            {packagesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-[#5A9BD5] animate-spin" />
                <span className="ml-3 text-gray-400">Loading packages...</span>
              </div>
            ) : packages.length === 0 ? (
              <Alert className="bg-yellow-500/10 border-yellow-500/30">
                <AlertDescription className="text-yellow-400">
                  No credit packages available at the moment. Please check back later.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {packages.map((pkg, index) => {
                  const bestValue = getBestValue(packages);
                  const isBestValue = pkg.id === bestValue?.id;
                  
                  return (
                    <Card 
                      key={pkg.id}
                      className={`relative bg-white/5 border-white/10 transition-all hover:bg-white/10 ${
                        isBestValue ? 'ring-2 ring-green-500' : ''
                      }`}
                    >
                      {isBestValue && (
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white">
                          Best Value
                        </Badge>
                      )}
                      <CardContent className="p-5 text-center">
                        <div className={`mx-auto w-14 h-14 rounded-full bg-gradient-to-br ${getPackageColor(index)} flex items-center justify-center text-white mb-4`}>
                          {getPackageIcon(index)}
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-1">{pkg.name}</h3>
                        <p className="text-gray-400 text-sm mb-3">{pkg.credits.toLocaleString()} credits</p>
                        
                        <div className="mb-4">
                          <span className="text-3xl font-bold text-white">${pkg.price_usd}</span>
                        </div>
                        
                        <p className="text-gray-500 text-xs mb-4">
                          ${(pkg.price_per_credit * 100).toFixed(2)} per 100 credits
                        </p>
                        
                        {/* Payment Button */}
                        {paymentMethod === 'stripe' && paymentMethods.stripe_enabled ? (
                          <Button
                            onClick={() => handleStripeCheckout(pkg.id)}
                            disabled={loading}
                            className={`w-full bg-gradient-to-r ${getPackageColor(index)} hover:opacity-90`}
                          >
                            {loading && selectedPackage === pkg.id ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <CreditCard className="mr-2 h-4 w-4" />
                                Buy Now
                              </>
                            )}
                          </Button>
                        ) : paymentMethod === 'paypal' && paymentMethods.paypal_enabled && paymentMethods.paypal_client_id ? (
                          <PayPalScriptProvider options={{ 
                            clientId: paymentMethods.paypal_client_id,
                            currency: "USD"
                          }}>
                            <div className="paypal-button-container">
                              {processingPayPal ? (
                                <Button disabled className="w-full bg-[#0070ba]">
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Processing...
                                </Button>
                              ) : (
                                <PayPalButtons
                                  style={{ 
                                    layout: "horizontal",
                                    color: "blue",
                                    shape: "rect",
                                    label: "pay",
                                    height: 35
                                  }}
                                  createOrder={() => createPayPalOrder(pkg.id)}
                                  onApprove={(data) => onPayPalApprove(data)}
                                  onError={(err) => {
                                    console.error('PayPal error:', err);
                                    toast.error('PayPal payment failed. Please try again.');
                                  }}
                                  onCancel={() => {
                                    toast.info('Payment cancelled');
                                  }}
                                />
                              )}
                            </div>
                          </PayPalScriptProvider>
                        ) : (
                          <Button disabled className="w-full bg-gray-600">
                            Select Payment Method
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Info */}
            <Alert className="bg-[#5A9BD5]/10 border-[#5A9BD5]/30 mt-6">
              <CreditCard className="h-4 w-4 text-[#5A9BD5]" />
              <AlertDescription className="text-gray-300">
                Credits are used to pay for trading fees. They never expire and can be used across all supported exchanges.
                Payments are securely processed by {paymentMethod === 'stripe' ? 'Stripe' : paymentMethod === 'paypal' ? 'PayPal' : 'our payment partners'}.
              </AlertDescription>
            </Alert>
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals" className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Earn Free Credits</h2>
              <p className="text-gray-400">Invite friends and earn credits when they sign up and trade.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Referral Link Card */}
              <Card className="bg-gradient-to-br from-[#5A9BD5]/20 to-[#5A9BD5]/5 border-[#5A9BD5]/30">
                <CardHeader>
                  <CardTitle className="text-white">Your Referral Link</CardTitle>
                  <CardDescription className="text-gray-400">Share this link with friends</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 p-3 bg-white/10 rounded-lg mb-4">
                    <code className="flex-1 text-sm text-[#5A9BD5] truncate">
                      {window.location.origin}/auth?ref={referralCode}
                    </code>
                    <Button 
                      size="sm" 
                      onClick={handleCopyReferralCode}
                      className="bg-[#5A9BD5] hover:bg-[#4A8BC5]"
                      data-testid="copy-referral-btn"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <Alert className="bg-white/5 border-white/10">
                    <Gift className="w-4 h-4 text-green-400" />
                    <AlertDescription className="text-gray-300">
                      Earn credits when your friends sign up and start trading!
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

              {/* Stats Card */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Your Referral Stats</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-white/5 rounded-lg text-center">
                      <div className="text-2xl font-bold text-white">{referralStats?.total_referrals || 0}</div>
                      <div className="text-gray-500 text-sm">Friends Invited</div>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg text-center">
                      <div className="text-2xl font-bold text-green-400">+{referralStats?.total_bonus_earned?.toFixed(2) || '0.00'}</div>
                      <div className="text-gray-500 text-sm">Credits Earned</div>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg text-center">
                      <div className="text-2xl font-bold text-white">{referralStats?.converted_referrals || 0}</div>
                      <div className="text-gray-500 text-sm">Active Traders</div>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg text-center">
                      <div className="text-2xl font-bold text-white">{referralStats?.pending_referrals || 0}</div>
                      <div className="text-gray-500 text-sm">Pending</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Account Tab */}
          <TabsContent value="account" className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Account Settings</h2>
              <p className="text-gray-400">Manage your account details.</p>
            </div>

            <Card className="bg-white/5 border-white/10 max-w-2xl mx-auto">
              <CardContent className="p-6 space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-white/10">
                    <div>
                      <div className="text-white font-medium">Email</div>
                      <div className="text-gray-400 text-sm">{user?.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-white/10">
                    <div>
                      <div className="text-white font-medium">Name</div>
                      <div className="text-gray-400 text-sm">{user?.name || 'Not set'}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-white/10">
                    <div>
                      <div className="text-white font-medium">Account Type</div>
                      <div className="text-gray-400 text-sm capitalize">{user?.role || 'User'}</div>
                    </div>
                    <Badge className={user?.role === 'admin' ? 'bg-purple-500' : 'bg-[#5A9BD5]'}>
                      {user?.role || 'User'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <div className="text-white font-medium">Member Since</div>
                      <div className="text-gray-400 text-sm">
                        {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-white/10">
                  <Button 
                    variant="outline" 
                    className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                    onClick={handleLogout}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default UserDashboard;
